import axios, {
  AxiosInstance,
  InternalAxiosRequestConfig,
  AxiosError,
} from 'axios';

// Base URL for backend
const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const api: AxiosInstance = axios.create({
  baseURL: BASE_URL,
  timeout: 300000,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // Useful if you later switch to cookie auth
});

// Request Interceptor – Add JWT token if available
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = localStorage.getItem('token');

    if (token) {
      // AxiosHeaders is mutable and safe to set
      config.headers.set('Authorization', `Bearer ${token}`);
    }

    return config;
  },
  (error: AxiosError) => {
    return Promise.reject(error);
  }
);

// Response Interceptor – Handle 401 (unauthorized)
api.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    if (error.response?.status === 401) {
      // Clear invalid/expired token and redirect to login
      localStorage.removeItem('token');
      window.location.href = '/login';
    }

    // Log only status/URL — never the response body, which may contain PHI.
    console.error('API Error:', error.response?.status, error.config?.url);
    return Promise.reject(error);
  }
);

/**
 * Authenticated file download. Fetches `url` (relative to the API base) as a blob
 * WITH the Authorization header, then triggers a browser save. Replaces the old
 * `window.open(baseURL + url)` pattern, which could not send auth and therefore
 * required the file to be served from a public (unauthenticated) endpoint.
 */
export async function downloadFile(url: string, filename?: string): Promise<void> {
  const res = await api.get(url, { responseType: 'blob' });
  const blobUrl = window.URL.createObjectURL(res.data as Blob);
  const a = document.createElement('a');
  a.href = blobUrl;
  a.download = filename || 'report';
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(blobUrl);
}

export default api;